"use client";

import {
	faro,
	getWebInstrumentations,
	initializeFaro,
	NavigationInstrumentation,
} from "@grafana/faro-web-sdk";
import React from "react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { useEffect, useCallback } from "react";
import {
	faroManager,
	syncUserContextFromCookies,
} from "@/lib/client/faro-manager";
import { getCookie } from "cookies-next/client";

// Define the minimal interface we need for the Faro API
interface FaroApi {
	getOTEL: () => {
		trace?: {
			getActiveSpan: () =>
				| {
						spanContext: () => {
							traceId: string;
							spanId: string;
							traceFlags: number;
						};
				  }
				| undefined;
		};
	};
}

// Extend Window interface to include our auth change handler
declare global {
	interface Window {
		handleObservabilityAuthChange?: () => void;
	}
}

export default function FrontendObservability() {
	const ref = React.useRef<number>(0);

	// Function to generate session ID from existing cookie values
	const generateSessionId = (
		email?: string,
		deviceId?: string,
		hasAuthorization?: boolean,
	) => {
		if (hasAuthorization && email && deviceId) {
			// Logged in user: combine email and deviceId
			return `${email}_${deviceId}`;
		} else if (deviceId) {
			// Guest user: use deviceId only
			return deviceId;
		} else {
			// Fallback: generate a temporary session ID
			return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		}
	};

	// Function to set user context from cookies
	const setUserContextFromCookies = useCallback(() => {
		if (!faroManager.isReady()) {
			console.debug("[Faro] Not ready yet, skipping user context update");
			return;
		}

		ref.current = ref.current + 1;

		// Read from cookies
		const hasAuthorization = Boolean(getCookie("Authorization"));
		const email = getCookie("email") as string;
		const deviceId = getCookie("deviceId") as string;
		const sessionId = generateSessionId(email, deviceId, hasAuthorization);

		console.debug(
			"[Faro] Updating user context from cookies",
			JSON.stringify({
				hasAuthorization,
				email,
				deviceId,
				sessionId,
			}),
		);

		if (hasAuthorization && email) {
			// User is logged in - use email as user ID, combined session ID
			faroManager.setUserContext({
				id: email, // User ID is email
				email: email,
				sessionId: sessionId, // Combined email_deviceId session ID
			});
		} else if (deviceId) {
			// User is not logged in - use deviceId as both user ID and session ID
			faroManager.setUserContext({
				id: deviceId, // User ID is deviceId
				guestUserId: deviceId,
				sessionId: sessionId, // DeviceId session ID
			});
		} else {
			console.log("Let's see what case is temp ID", ref.current);
			// No user context available - use temporary session ID
			faroManager.setUserContext({
				id: sessionId, // Fallback to session ID
				sessionId: sessionId,
			});
		}
	}, []);

	// Initialize Faro
	useEffect(() => {
		// Check if instrumentation is disabled
		// Note: Client components only have access to NEXT_PUBLIC_* env vars
		const disableFlag = process.env.NEXT_PUBLIC_DISABLE_INSTRUMENTATION;
		const isDisabled =
			disableFlag === "true" || disableFlag === "1" || disableFlag === "yes";

		if (isDisabled) {
			console.debug(
				"[Faro] Instrumentation is disabled via DISABLE_INSTRUMENTATION flag",
			);
			return;
		}

		if (faro.api) {
			console.debug("[Faro] Already initialized");
			faroManager.markInitialized();
			setUserContextFromCookies();
			return;
		}

		try {
			console.debug(
				"[Faro] Initializing with URL:",
				process.env.NEXT_PUBLIC_FARO_URL,
			);

			initializeFaro({
				url: process.env.NEXT_PUBLIC_FARO_URL,
				app: {
					name:
						process.env.NEXT_PUBLIC_FARO_APP_NAME || "unknown_service:webjs",
					namespace: process.env.NEXT_PUBLIC_FARO_APP_NAMESPACE || undefined,
					version: "1.0.0",
					environment: process.env.NODE_ENV || "development",
					bundleId: process.env.NEXT_PUBLIC_FARO_BUNDLE_ID || undefined,
				},
				instrumentations: [
					...getWebInstrumentations(),
					new TracingInstrumentation(),
					new NavigationInstrumentation(),
				],
				// Configure console instrumentation to capture all logs
				consoleInstrumentation: {
					disabledLevels: [], // Enable all console methods
				},
				// Exclude list for error tracking - ignore errors from these URLs
				ignoreUrls: [
					// Google Analytics related URLs
					/google-analytics\.com/i,
					/googletagmanager\.com/i,
					/ga\.js/i,
					/analytics\.js/i,
					/gtag/i,
					/gtm\.js/i,
				],
				// Ignore errors with these specific messages
				ignoreErrors: [
					/Script error/i,
					/ResizeObserver loop/i,
					// Add more common error patterns here as needed
				],
				// Enable debug mode in development
				beforeSend: (event) => {
					if (process.env.NODE_ENV === "development") {
						console.debug("[Faro] Sending event:", event);
					}
					return event;
				},
			});

			// Verify initialization
			if (faro.api) {
				const otel = (faro.api as FaroApi).getOTEL();
				console.debug(
					"[Faro] Initialized successfully. OTEL available:",
					!!otel,
				);
				if (otel?.trace) {
					const span = otel.trace.getActiveSpan();
					console.debug("[Faro] Active span:", span?.spanContext());
				}
				faroManager.setFaro(faro);
				faroManager.markInitialized();

				// Set initial user context from cookies
				setUserContextFromCookies();
			}
		} catch (e) {
			console.error(
				"[Faro] Error while initializing observability in Frontend.",
				e,
			);
		}
	}, [setUserContextFromCookies]);

	// Export the handler for use in auth-related components
	useEffect(() => {
		// Make handler available globally for auth events
		window.handleObservabilityAuthChange = syncUserContextFromCookies;

		return () => {
			delete window.handleObservabilityAuthChange;
		};
	}, []);

	return null;
}
